import { useOffline } from '@/components/offline-context'
import { useInfiniteQuery } from '@tanstack/react-query'
import { searchOffline, searchOnline } from '../lib/search-service'
import { useSettings } from './useSettings'
import { useLocale } from 'next-intl'
import type { SearchResult } from '@/types/dictionary'

export function useSearch(
  query: string,
  englishMode: boolean,
  isOfflineMode: boolean,
  radical?: number | null
) {
  const { settings } = useSettings()
  const { isQueryEnabled } = useOffline()
  const uiLocale = useLocale()

  // Compute effective definition locale
  const effectiveLocale =
    settings.definitionLocale === 'auto'
      ? uiLocale === 'es'
        ? 'es'
        : 'en' // Default to English for non-Spanish UI locales
      : settings.definitionLocale

  return useInfiniteQuery({
    queryKey: [
      'search',
      query,
      englishMode,
      isOfflineMode,
      settings.scriptDisplay,
      radical,
      effectiveLocale
    ],
    initialPageParam: 0,
    getNextPageParam: (lastPage: SearchResult[], allPages) => {
      // If last page has fewer results than limit, we are done
      return lastPage.length === 50 ? allPages.length : undefined
    },

    queryFn: async ({ pageParam = 0 }) => {
      if (!query.trim()) return []

      try {
        if (isOfflineMode) {
          return await searchOffline(query, englishMode, pageParam, 50, radical)
        }
        return await searchOnline(
          query,
          englishMode,
          pageParam,
          50,
          radical,
          effectiveLocale
        )
      } catch (err) {
        console.error('Search Failed:', err)
        // Optional: Fallback logic (If online fails, try offline?)
        throw err
      }
    },

    enabled: isQueryEnabled(query.trim().length > 0),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 30, // Cache results for 30 minutes - dictionary data is static
    refetchOnMount: false, // Don't refetch when component mounts if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    gcTime: 1000 * 60 * 60 // Keep in cache for 1 hour
  })
}
